import { useId } from "react";
import { Plus, Trash2 } from "lucide-react";
import { Button } from "@components/ui/button";
import { Input } from "@components/ui/input";
import { Label } from "@components/ui/label";
import { ChoiceSelect } from "@components/ui/choice-select";

export type FieldMappingRow = { id: string; field: string; columnId: string };

const fields = [
  ["description", "Description"],
  ["status", "Status"],
  ["priority", "Priority"],
  ["owner", "Owner"],
  ["systems", "Systems"],
  ["customerImpact", "Customer impact"],
] as const;

export function FieldMappingEditor({ rows, onChange }: { rows: FieldMappingRow[]; onChange: (rows: FieldMappingRow[]) => void }) {
  const baseId = useId();

  const add = () => {
    const nextField = fields.find(([field]) => !rows.some((row) => row.field === field))?.[0] ?? "description";
    onChange([...rows, { id: crypto.randomUUID(), field: nextField, columnId: "" }]);
  };

  return (
    <fieldset className="space-y-3 rounded-lg border p-3">
      <legend className="px-1 text-sm font-medium">Board column mappings</legend>
      <p className="text-sm text-muted-foreground">Map only the fields this board uses. Find column IDs in monday.com developer mode.</p>
      {rows.map((row, index) => (
        <div className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto]" key={row.id}>
          <div>
            <Label className="sr-only" htmlFor={`${baseId}-field-${row.id}`}>IO Spoke field</Label>
            <ChoiceSelect
              id={`${baseId}-field-${row.id}`}
              aria-label={`IO Spoke field ${index + 1}`}
              value={row.field}
              onValueChange={(value) => onChange(rows.map((item) => (item.id === row.id ? { ...item, field: value } : item)))}
             options={fields.map(([value, label]) => ({ value, label }))} />
          </div>
          <div>
            <Label className="sr-only" htmlFor={`${baseId}-column-${row.id}`}>monday.com column ID</Label>
            <Input
              id={`${baseId}-column-${row.id}`}
              aria-label={`monday.com column ID ${index + 1}`}
              placeholder="Column ID"
              value={row.columnId}
              onChange={(event) => onChange(rows.map((item) => (item.id === row.id ? { ...item, columnId: event.target.value } : item)))}
            />
          </div>
          <Button type="button" variant="outline" size="icon" aria-label={`Remove mapping ${index + 1}`} onClick={() => onChange(rows.filter((item) => item.id !== row.id))}>
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      ))}
      <Button type="button" variant="outline" onClick={add} disabled={rows.length >= fields.length}>
        <Plus className="mr-2 h-4 w-4" />
        Add mapping
      </Button>
    </fieldset>
  );
}
